import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import Seo from "@/seo/Seo";
import { useAuth } from "@/context/AuthContext";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    const res = await resetPassword(token, password);
    setBusy(false);
    if (res.ok) {
      setDone(true);
      toast.success("Password reset");
    } else {
      toast.error(res.error || "Something went wrong");
    }
  }

  return (
    <>
      <Seo title="Reset password — FloorMatch" description="Choose a new password for your FloorMatch account." path="/reset-password" />
      <PublicHeader />
      <main className="min-h-screen bg-slate-50 py-10" data-testid="reset-password-page">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            {!token ? (
              <div className="text-center" data-testid="reset-no-token">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
                <h1 className="font-display font-bold text-xl text-slate-900 mt-3">Reset link missing</h1>
                <p className="text-sm text-slate-500 mt-1">
                  This page needs a valid reset link. Please request a new one from the sign-in page.
                </p>
                <Link to="/account" className="mt-5 inline-flex items-center justify-center h-11 px-5 rounded-full bg-[#0F3D2E] text-white font-semibold" data-testid="reset-back-signin">
                  Back to sign in
                </Link>
              </div>
            ) : done ? (
              <div className="text-center" data-testid="reset-success">
                <CheckCircle2 className="w-10 h-10 text-[#2A5A3B] mx-auto" />
                <h1 className="font-display font-bold text-xl text-slate-900 mt-3">Password reset</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Your password has been updated. Please sign in with your new password.
                </p>
                <button
                  onClick={() => navigate("/account")}
                  className="mt-5 inline-flex items-center justify-center h-11 px-5 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold"
                  data-testid="reset-go-signin"
                >
                  Go to sign in
                </button>
              </div>
            ) : (
              <>
                <h1 className="font-display font-bold text-2xl text-slate-900">Choose a new password</h1>
                <p className="text-sm text-slate-500 mt-1">Enter a new password for your FloorMatch account.</p>
                <form onSubmit={submit} className="space-y-3 mt-5" data-testid="reset-form">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                      <Lock className="w-3.5 h-3.5" /> New password
                    </span>
                    <input
                      type="password"
                      className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      data-testid="reset-password-input"
                    />
                    <span className="text-[11px] text-slate-400 mt-1 block">At least 8 characters.</span>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                      <Lock className="w-3.5 h-3.5" /> Confirm new password
                    </span>
                    <input
                      type="password"
                      className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      data-testid="reset-confirm-input"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full h-12 rounded-full bg-[#0F3D2E] hover:bg-[#0c3325] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70"
                    data-testid="reset-submit"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Reset password
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
